import React from 'react';

import { useTranslation } from 'react-i18next';

import { type Label } from '@/app/services/fertWork';
import { getEmbryoCategoryColorClass } from '@/utils/categoryColors';

export type LabelCardProps = {
  label: Label;
  secondLabel?: Label;
  displayLabelId: string;
  stripeText: string;
  sessionId: string;
  readOnly?: boolean;
  fourMating?: boolean;
  isCommercial?: boolean;
  onPrintLabel?: (labelId: string) => void;
  isDuplicate?: boolean;
  renderMatingWrapper?: (props: {
    labelId: string;
    position: string;
    detailId: string;
    children: React.ReactNode;
  }) => React.ReactNode;
  renderEmptyDropZone?: (props: {
    labelId: string;
    position: string;
  }) => React.ReactNode;
  isOverlay?: boolean;
};

const deriveMatingCategory = (
  label: Label,
  shipLabel?: string | null,
): { planCategory: string; isDay5: boolean } => {
  const top = label.top?.shipLabel ?? '';
  const bottom = label.bottom?.shipLabel ?? '';
  const differs = !!label.top && !!label.bottom && top !== bottom;
  if (differs && shipLabel != null) {
    const s = shipLabel.toLowerCase();
    if (s === 'freezeonly' || s === 'freeze only' || s === 'freeze') {
      return { planCategory: 'FreezeOnly', isDay5: label.isDay5 };
    }
    if (s.includes('ship')) {
      return { planCategory: 'Ship', isDay5: label.isDay5 };
    }
  }
  return {
    planCategory: label.planCategory ?? '',
    isDay5: label.isDay5,
  };
};

const getMatingAccentClass = (
  label: Label,
  shipLabel?: string | null,
): string => {
  const { planCategory, isDay5 } = deriveMatingCategory(label, shipLabel);
  return getEmbryoCategoryColorClass({ isDay5, planCategory });
};

const getMatingTextClass = (
  label: Label,
  shipLabel?: string | null,
): string => {
  const { planCategory, isDay5 } = deriveMatingCategory(label, shipLabel);
  // Small Ruminant species color wins over plan category (per stakeholder feedback
  // 2026-04-27): goats render as green text, sheep as orange text on plates and maps.
  const species = getLabelSpecies(label);
  const speciesText = getSpeciesTextClass(species);
  if (speciesText) return speciesText;
  if (isDay5) return 'text-purple-900';
  if (planCategory === 'ExportNonEU') return 'text-red-600';
  if (planCategory?.startsWith('Export')) return 'text-amber-900';
  if (planCategory === 'FreezeOnly') return 'text-blue-900';
  return 'text-gray-900';
};

const getSplitMarker = (donorName?: string): string | null => {
  if (!donorName) return null;
  const match = donorName.match(/\((\d+\+\s*\d+\/\d+|\d+\/\d+)\)/);
  return match?.[1] ?? null;
};

// Mirrors FertWorkLabelsDocument.IsIvcLabel on the report/PDF side: Priority is
// only ever meaningful for IVC/SOF labels (WI 35151).
const isIvcStripe = (stripeText: string): boolean =>
  /ivc|sof/i.test(stripeText);

const getLabelSpecies = (label: Label): string | null => {
  const s =
    label.top?.speciesSubfamily ?? label.bottom?.speciesSubfamily ?? null;
  if (!s || s === 'Bovine') return null;
  return s;
};

const getSpeciesStripeClass = (species: string | null): string => {
  if (species === 'Ovine') return 'bg-orange-500';
  if (species === 'Caprine') return 'bg-green-500';
  return '';
};

const getSpeciesBadge = (
  species: string | null,
): { code: string; className: string } | null => {
  if (species === 'Ovine')
    return {
      code: 'OV',
      className: 'bg-orange-100 text-orange-800 border-orange-300',
    };
  if (species === 'Caprine')
    return {
      code: 'CAP',
      className: 'bg-green-100 text-green-800 border-green-300',
    };
  return null;
};

// Per stakeholder (2026-04-27): donor + sire text on SR plates renders in the species color
// (orange for Ovine, green for Caprine) so the mat-time mix-up risk is visually obvious.
const getSpeciesTextClass = (species: string | null): string | null => {
  if (species === 'Ovine') return 'text-orange-700';
  if (species === 'Caprine') return 'text-green-700';
  return null;
};

export const LabelCard = ({
  label,
  secondLabel,
  displayLabelId,
  stripeText,
  readOnly = false,
  fourMating = false,
  isCommercial = false,
  onPrintLabel,
  isDuplicate = false,
  renderMatingWrapper,
  renderEmptyDropZone,
  isOverlay = false,
}: LabelCardProps) => {
  const { t } = useTranslation();
  const isMassProduction = !!label.isMassProduction;
  const hasDifferentSires =
    !label.isHalfPlate &&
    label.top?.sireName &&
    label.bottom?.sireName &&
    label.top.sireName !== label.bottom.sireName;

  function renderMatingFromLabel(
    sourceLabel: Label,
    position: 'top' | 'bottom',
    compact = false,
  ) {
    const mating = sourceLabel[position];
    if (!mating) {
      if (!readOnly && renderEmptyDropZone) {
        return renderEmptyDropZone({ labelId: sourceLabel.id, position });
      }
      return null;
    }

    const textClass = getMatingTextClass(sourceLabel, mating.shipLabel);
    const isMp = !!sourceLabel.isMassProduction;
    const splitMarker = getSplitMarker(mating.donorName);
    const { planCategory: derivedCategory, isDay5: derivedDay5 } =
      deriveMatingCategory(sourceLabel, mating.shipLabel);
    const badgeText = derivedDay5
      ? t('fertWork.day5Badge', 'DAY 5')
      : derivedCategory === 'ExportNonEU'
        ? 'Non-EU'
        : derivedCategory?.startsWith('Export')
          ? derivedCategory.replace('Export', '')
          : null;
    const badgeClass = derivedDay5
      ? 'bg-purple-500 text-white'
      : derivedCategory === 'ExportNonEU'
        ? 'bg-red-500 text-white'
        : 'bg-amber-500 text-white';
    const content = (
      <div
        className={`flex h-full flex-col justify-center rounded-l-none rounded-r-md border ${compact ? 'p-1.5' : 'p-3'} ${getMatingAccentClass(sourceLabel, mating.shipLabel)}`}
      >
        <div
          className={`${compact ? 'space-y-0.5 text-xs' : 'space-y-1 text-sm'} leading-snug`}
        >
          <div
            className={`flex items-start justify-between gap-1 ${textClass}`}
          >
            <div className="break-words">
              {!isMp && <span className="font-medium text-gray-600">D:</span>}{' '}
              <span className="font-semibold">
                {isMp && mating.poolNumber != null
                  ? `Pool-${mating.poolNumber}`
                  : mating.donorName}
              </span>
              {isCommercial &&
                isIvcStripe(stripeText) &&
                mating.priority != null && (
                  <span className="ml-1 rounded bg-amber-50 px-1 py-[1px] text-[9px] font-semibold text-amber-700 ring-1 ring-amber-200">
                    P{mating.priority}
                  </span>
                )}
            </div>
            {badgeText && (
              <span
                className={`mt-0.5 flex-shrink-0 rounded px-1.5 py-0.5 ${compact ? 'text-[8px]' : 'text-xs'} font-bold ${badgeClass}`}
              >
                {badgeText}
              </span>
            )}
          </div>
          <div className={`break-words ${textClass}`}>
            <span className="font-medium text-gray-600">S:</span>{' '}
            <span className="font-semibold">{mating.sireName}</span>
          </div>
          <div
            className={`${compact ? 'text-[10px]' : 'text-xs'} break-words ${textClass}`}
          >
            <span className="font-medium text-gray-600">SL:</span>{' '}
            {mating.shipLabel || '-'}
          </div>
          <div
            className={`pt-0.5 ${compact ? 'text-[9px]' : 'text-[11px]'} font-semibold tracking-wide text-gray-500`}
          >
            {mating.oocytes
              ? `${mating.oocytes} ${t('fertWork.oocytes', 'oocytes')}${splitMarker ? ` • ${splitMarker}` : ''}`
              : '\u00A0'}
          </div>
        </div>
      </div>
    );

    if (!readOnly && renderMatingWrapper) {
      return renderMatingWrapper({
        labelId: sourceLabel.id,
        position,
        detailId: mating.detailId,
        children: content,
      });
    }

    return content;
  }

  function renderMating(position: 'top' | 'bottom') {
    return renderMatingFromLabel(label, position);
  }

  // Four-mating quadrant layout
  if (fourMating && secondLabel) {
    const labelSpecies = getLabelSpecies(label);
    const speciesStripeClass = getSpeciesStripeClass(labelSpecies);
    const speciesBadge = getSpeciesBadge(labelSpecies);

    return (
      <div
        className={`relative flex h-full flex-col ${isMassProduction ? 'border-2 border-gray-900' : 'border border-gray-200'} ${label.isDay5 ? 'bg-purple-50' : 'bg-white'} rounded-r-lg p-2 pl-6 transition-shadow hover:shadow-md ${isOverlay ? 'shadow-xl' : 'shadow-sm'}`}
      >
        {speciesStripeClass && (
          <div
            className={`absolute top-0 bottom-0 left-0 w-2 rounded-l-lg ${speciesStripeClass}`}
          />
        )}
        {onPrintLabel && (
          <button
            type="button"
            className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
            onClick={(e) => {
              e.stopPropagation();
              onPrintLabel(label.id);
            }}
            title={t('fertWork.printLabel', 'Print label')}
          >
            <i className="pi pi-print text-xs" />
          </button>
        )}

        <div className="mb-1 flex items-center gap-2 pr-8">
          <span className="text-xs font-medium text-gray-700">
            {displayLabelId}
          </span>
          {label.runNumber > 0 && (
            <span className="rounded border border-slate-300 bg-slate-100 px-1 py-0.5 text-[8px] font-semibold tracking-wide text-slate-700">
              RUN-{label.runNumber}
            </span>
          )}
          {speciesBadge && (
            <span
              className={`rounded border px-1 py-0.5 text-[8px] font-bold tracking-wide ${speciesBadge.className}`}
            >
              {speciesBadge.code}
            </span>
          )}
        </div>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-2 gap-1">
            <div className="min-h-0 overflow-hidden">
              {renderMatingFromLabel(label, 'top', true)}
            </div>
            <div className="min-h-0 overflow-hidden">
              {renderMatingFromLabel(label, 'bottom', true)}
            </div>
          </div>

          <div
            className={`my-1 flex items-center justify-center rounded-md border py-1 ${
              hasDifferentSires || label.isHalfPlate
                ? 'border-red-300 bg-red-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <span className="text-xs font-medium text-gray-700">
              {stripeText}
            </span>
          </div>

          <div className="grid min-h-0 flex-1 grid-cols-2 gap-1">
            <div className="min-h-0 overflow-hidden">
              {renderMatingFromLabel(secondLabel, 'top', true)}
            </div>
            <div className="min-h-0 overflow-hidden">
              {renderMatingFromLabel(secondLabel, 'bottom', true)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bottomContent = renderMating('bottom');
  const showBottomSection = bottomContent !== null;
  // An empty position renders only a fixed-height drop zone, so it must size to
  // its content instead of claiming an equal flex share. Otherwise a half plate
  // starves the populated half, which then overflows onto the IVF/IVC stripe.
  const topIsEmpty = !label.top;
  const bottomIsEmpty = !label.bottom;
  const sectionGrowClass = (isEmpty: boolean) =>
    isEmpty ? 'flex-none' : 'flex-1';
  const labelSpecies = getLabelSpecies(label);
  const speciesStripeClass = getSpeciesStripeClass(labelSpecies);
  const speciesBadge = getSpeciesBadge(labelSpecies);

  return (
    <div
      className={`relative flex h-full flex-col ${isMassProduction ? 'border-2 border-gray-900' : 'border border-gray-200'} ${label.isDay5 ? 'bg-purple-50' : 'bg-white'} rounded-r-lg p-4 pl-8 transition-shadow hover:shadow-md ${isOverlay ? 'shadow-xl' : 'shadow-sm'}`}
    >
      {speciesStripeClass && (
        <div
          className={`absolute top-0 bottom-0 left-0 w-2 rounded-l-lg ${speciesStripeClass}`}
        />
      )}
      {onPrintLabel && (
        <button
          type="button"
          className="absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition-colors hover:bg-gray-200 hover:text-gray-800"
          onClick={(e) => {
            e.stopPropagation();
            onPrintLabel(label.id);
          }}
          title={t('fertWork.printLabel', 'Print label')}
        >
          <i className="pi pi-print text-sm" />
        </button>
      )}

      <div className="mb-3 flex items-center justify-between pr-10">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-700">{displayLabelId}</span>
          {label.runNumber > 0 && (
            <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold tracking-wide text-slate-700">
              RUN-{label.runNumber}
            </span>
          )}
          {speciesBadge && (
            <span
              className={`rounded border px-1.5 py-0.5 text-[10px] font-bold tracking-wide ${speciesBadge.className}`}
            >
              {speciesBadge.code}
            </span>
          )}
          {isMassProduction && (
            <span className="rounded border border-gray-800 bg-gray-900 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white">
              MASS PROD
            </span>
          )}
          {isDuplicate && (
            <span className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-amber-700">
              + DUPLICATE
            </span>
          )}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <div
          className={`flex min-h-0 ${showBottomSection ? sectionGrowClass(topIsEmpty) : ''} flex-col justify-center`}
        >
          {renderMating('top')}
        </div>

        <div
          className={`my-2 flex items-center justify-center rounded-md border py-2 ${
            hasDifferentSires || label.isHalfPlate
              ? 'border-red-300 bg-red-50'
              : 'border-gray-200 bg-gray-50'
          }`}
        >
          <span className="text-sm font-medium text-gray-700">
            {stripeText}
          </span>
        </div>

        {showBottomSection && (
          <div
            className={`flex min-h-0 ${sectionGrowClass(bottomIsEmpty)} flex-col justify-center`}
          >
            {bottomContent}
          </div>
        )}
      </div>
    </div>
  );
};
